/**
 * Alert components - daisyUI alert wrappers with icon support.
 * Provides InfoAlert, WarningAlert, and ErrorAlert variants.
 * @module components/ui/Alert
 */

import type { ReactNode } from "react";
import { Alert as DaisyAlert, type AlertProps as DaisyAlertProps } from "react-daisyui";

import { Icon } from "./Icon";

export interface AlertProps {
  /**
   * A one-line heading above the body. Styled text, not a heading element: an
   * alert sits at whatever level its screen is at, so it must not claim one.
   */
  title?: ReactNode;
  /** Alert content */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /**
   * react-daisyui hardcodes `role="alert"` and spreads props after it, so this
   * is the only way to change it. Defaults to `alert` for an error or a
   * warning, which is how most screens report a failure; info and success are
   * guidance with no news to interrupt with.
   * `status` is the polite form, for an alert that reports an outcome the
   * reader is already looking at. `none` is for an alert inside a live region
   * of its own, which announces it once rather than twice.
   */
  role?: "alert" | "status" | "none";
  /** Test ID for the alert */
  "data-testid"?: string;
}

/** The tone vocabulary the rest of the suite uses. */
export type AlertTone = "info" | "success" | "warning" | "error" | "neutral";

export type CustomAlertProps = AlertProps & {
  /** Optional icon name */
  icon?: string;
  tone?: AlertTone;
};

/** Each tone as a react-daisyui status plus any extra alert classes. */
const TONE_APPEARANCE: Record<AlertTone, { status?: DaisyAlertProps["status"]; extra?: string }> = {
  info: { status: "info", extra: "alert-soft" },
  success: { status: "success", extra: "alert-soft" },
  warning: { status: "warning", extra: "alert-soft" },
  error: { status: "error", extra: "alert-soft" },
  neutral: { extra: "alert-neutral alert-soft" },
};

/**
 * Base Alert component with a tone and an optional icon.
 */
function announcedRole(role: AlertProps["role"], tone: AlertTone): "alert" | "status" | undefined {
  if (role === "none") {
    return undefined;
  }
  if (role) {
    return role;
  }
  return tone === "error" || tone === "warning" ? "alert" : undefined;
}

export function Alert({
  tone = "info",
  icon = undefined,
  title = undefined,
  children,
  className = "",
  role = undefined,
  "data-testid": testId,
}: CustomAlertProps) {
  const { status, extra } = TONE_APPEARANCE[tone];
  return (
    <DaisyAlert
      status={status}
      // `undefined` removes the role react-daisyui hardcodes, which is the point.
      role={announcedRole(role, tone)}
      className={[extra, className].filter(Boolean).join(" ") || undefined}
      data-testid={testId}
    >
      {icon && <Icon name={icon} className="mr-2" />}
      <div>
        {title ? <p className="font-semibold">{title}</p> : null}
        {children}
      </div>
    </DaisyAlert>
  );
}

/**
 * Info alert with lightbulb icon.
 */
export function InfoAlert({
  title = undefined,
  children,
  className = undefined,
  role = undefined,
  "data-testid": testId,
}: AlertProps) {
  return (
    <Alert
      tone="info"
      icon="lightbulb"
      title={title}
      className={className}
      role={role}
      data-testid={testId}
    >
      {children}
    </Alert>
  );
}

/**
 * Warning alert with warning icon.
 */
export function WarningAlert({
  title = undefined,
  children,
  className = undefined,
  role = undefined,
  "data-testid": testId,
}: AlertProps) {
  return (
    <Alert
      tone="warning"
      icon="warning"
      title={title}
      className={className}
      role={role}
      data-testid={testId}
    >
      {children}
    </Alert>
  );
}

/**
 * Error alert with warning-circle icon.
 */
export function ErrorAlert({
  title = undefined,
  children,
  className = undefined,
  role = undefined,
  "data-testid": testId,
}: AlertProps) {
  return (
    <Alert
      tone="error"
      icon="warning-circle"
      title={title}
      className={className}
      role={role}
      data-testid={testId}
    >
      {children}
    </Alert>
  );
}

/**
 * Success alert with check-circle icon.
 */
export function SuccessAlert({
  title = undefined,
  children,
  className = undefined,
  role = undefined,
  "data-testid": testId,
}: AlertProps) {
  return (
    <Alert
      tone="success"
      icon="check-circle"
      title={title}
      className={className}
      role={role}
      data-testid={testId}
    >
      {children}
    </Alert>
  );
}

export default Alert;
